import { roleAtLeast } from "@fts/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { BottomBar } from "./components/BottomBar";
import { Icon } from "./components/Icon";
import { QuickSearch, isQuickSearchShortcut } from "./components/QuickSearch";
import { Rail } from "./components/Rail";
import { TopBar, type Crumb } from "./components/TopBar";
import { useDrawer } from "./components/useDrawer";
import { Loading, EmptyState } from "./components/ui";
import type { DataProvider } from "./data";
import { createProvider } from "./data";
import { ToastProvider } from "./components/Toast";
import { I18nProvider, useI18n, type Lang, type MessageKey, type TFn } from "./i18n";
import { sitePath } from "./lib/fleet";
import { PLATFORM_ROUTES, ROUTE_MIN_ROLE } from "./nav/menu";
import { useRoute, type Route, type RouteName } from "./router/hash";
import { AlarmDetail } from "./screens/AlarmDetail";
import { AlarmsList } from "./screens/Alarms";
import { Dashboard } from "./screens/Dashboard";
import { Devices } from "./screens/Devices";
import { Inventory } from "./screens/Inventory";
import { LiveMap } from "./screens/LiveMap";
import { CardsScreen, OperatorDetail, OperatorsList, PermissionsScreen } from "./screens/Operators";
import { InvitePage } from "./screens/InvitePage";
import { ChecklistFaultsPage } from "./screens/ChecklistFaults";
import { ChecklistFormPage } from "./screens/ChecklistForm";
import { ChecklistTemplatesPage } from "./screens/ChecklistTemplates";
import { Login } from "./screens/Login";
import { ProfilePage } from "./screens/Profile";
import { PwaStatus } from "./pwa/PwaStatus";
import { AccessDenied, ComingSoon, NotFound } from "./screens/Placeholder";
import { SiteDetail, SitesList } from "./screens/Sites";
import { Users } from "./screens/Users";
import { ChecklistReport } from "./screens/reports/ChecklistReport";
import { EventsReport } from "./screens/reports/EventsReport";
import { HeatmapReport } from "./screens/reports/HeatmapReport";
import { SpaghettiReport } from "./screens/reports/SpaghettiReport";
import { DrivingReport } from "./screens/reports/DrivingReport";
import { BatteryReport } from "./screens/reports/BatteryReport";
import { LiftReport } from "./screens/reports/LiftReport";
import { KysFr11Report } from "./screens/reports/KysFr11Report";
import { KysFr12Report } from "./screens/reports/KysFr12Report";
import { RouteReport } from "./screens/reports/RouteReport";
import { UsageReport } from "./screens/reports/UsageReport";
import { VehicleDetail, VehiclesList } from "./screens/Vehicles";
import { AlarmCountProvider, useAlarmCount } from "./state/alarms";
import { SessionProvider, useSession, type TenantData } from "./state/session";

/** Translation key of the feature name for each route (used in titles and placeholders). */
const FEATURE_KEY: Record<Exclude<RouteName, "notFound" | "invite" | "checklistForm">, MessageKey> = {
  dashboard: "nav.overview",
  sites: "nav.sites",
  site: "nav.sites",
  vehicles: "nav.vehicles",
  vehicle: "nav.vehicles",
  users: "nav.users",
  map: "nav.liveMap",
  alarms: "nav.alarms",
  alarm: "nav.alarms",
  devices: "nav.devices",
  inventory: "nav.inventory",
  operators: "nav.operators",
  operator: "nav.operators",
  cards: "nav.cards",
  permissions: "nav.permissions",
  reportUsage: "nav.reportUsage",
  reportEvents: "nav.reportEvents",
  reportRoutes: "nav.reportRoutes",
  reportChecklist: "nav.reportChecklist",
  reportHeatmap: "nav.reportHeatmap",
  reportSpaghetti: "nav.reportSpaghetti",
  reportDriving: "nav.reportDriving",
  reportBattery: "nav.reportBattery",
  reportLift: "nav.reportLift",
  kysFr12: "nav.kysFr12",
  kysFr11: "nav.kysFr11",
  audit: "nav.audit",
  settings: "nav.settings",
  checklistFaults: "nav.checklistFaults",
  checklistTemplates: "nav.checklistTemplates",
  profile: "nav.profile",
};

/** The href of the menu node that should be highlighted for a route. */
export function activeHrefFor(route: Route): string {
  if (route.name === "site") return `#/sites/${encodeURIComponent(route.params.id ?? "")}`;
  if (route.name === "vehicle") return `#/vehicles/${encodeURIComponent(route.params.id ?? "")}`;
  if (route.name === "operator") return "#/operators";
  if (route.name === "alarm") return "#/alarms";
  return "#" + route.path;
}

function crumbsFor(route: Route, data: TenantData | null, tenantName: string, t: TFn): Crumb[] {
  const root: Crumb = { label: tenantName, href: "#/" };
  if (route.name === "notFound" || route.name === "invite" || route.name === "checklistForm") return [root, { label: t("soon.unknownTitle") }];
  const feature = t(FEATURE_KEY[route.name]);
  if (route.name === "dashboard") return [root, { label: feature }];
  if (route.name === "site") {
    const path = data ? sitePath(data.sites, route.params.id ?? "") : [];
    return [root, { label: t("nav.sites"), href: "#/sites" }, ...path.map((s) => ({ label: s.name, href: `#/sites/${encodeURIComponent(s.id)}` }))];
  }
  if (route.name === "operator") {
    const o = data?.operators.operators.find((x) => x.id === route.params.id);
    return [root, { label: t("nav.operators"), href: "#/operators" }, ...(o ? [{ label: o.fullName }] : [])];
  }
  if (route.name === "alarm") return [root, { label: t("nav.alarms"), href: "#/alarms" }, { label: `#${route.params.id ?? ""}` }];
  if (route.name === "vehicle") {
    const v = data?.vehicles.find((x) => x.id === route.params.id);
    return [root, { label: t("nav.vehicles"), href: "#/vehicles" }, ...(v ? [{ label: v.fleetNo }] : [])];
  }
  return [root, { label: feature }];
}

function Screen({ route, data }: { route: Route; data: TenantData }) {
  const { t } = useI18n();
  const { role, platformAdmin } = useSession();
  if (route.name === "notFound" || route.name === "invite" || route.name === "checklistForm") return <NotFound />;
  const min = ROUTE_MIN_ROLE[route.name];
  if (min && (!role || !roleAtLeast(role, min))) return <AccessDenied />;
  if (PLATFORM_ROUTES.has(route.name) && !platformAdmin) return <AccessDenied />;
  switch (route.name) {
    case "dashboard":
      return <Dashboard data={data} route={route} />;
    case "sites":
      return <SitesList data={data} />;
    case "site":
      return <SiteDetail data={data} siteId={route.params.id ?? ""} />;
    case "vehicles":
      return <VehiclesList data={data} route={route} />;
    case "vehicle":
      return <VehicleDetail data={data} vehicleId={route.params.id ?? ""} tab={route.params.tab} />;
    case "users":
      return <Users data={data} />;
    case "devices":
      return <Devices data={data} />;
    case "inventory":
      return <Inventory />;
    case "alarms":
      return <AlarmsList data={data} route={route} />;
    case "alarm":
      return <AlarmDetail data={data} alarmId={route.params.id ?? ""} />;
    case "map":
      return <LiveMap data={data} route={route} />;
    case "operators":
      return <OperatorsList data={data} />;
    case "operator":
      return <OperatorDetail data={data} operatorId={route.params.id ?? ""} />;
    case "cards":
      return <CardsScreen data={data} />;
    case "permissions":
      return <PermissionsScreen data={data} />;
    case "checklistFaults":
      return <ChecklistFaultsPage data={data} />;
    case "checklistTemplates":
      return <ChecklistTemplatesPage data={data} />;
    case "reportUsage":
      return <UsageReport data={data} route={route} />;
    case "reportEvents":
      return <EventsReport data={data} route={route} />;
    case "reportRoutes":
      return <RouteReport data={data} route={route} />;
    case "reportChecklist":
      return <ChecklistReport data={data} route={route} />;
    case "reportHeatmap":
      return <HeatmapReport data={data} route={route} />;
    case "reportSpaghetti":
      return <SpaghettiReport data={data} route={route} />;
    case "reportDriving":
      return <DrivingReport data={data} route={route} />;
    case "reportBattery":
      return <BatteryReport data={data} route={route} />;
    case "reportLift":
      return <LiftReport data={data} route={route} />;
    case "kysFr12":
      return <KysFr12Report data={data} route={route} />;
    case "kysFr11":
      return <KysFr11Report data={data} route={route} />;
    case "profile":
      return <ProfilePage />;
    default:
      return <ComingSoon feature={t(FEATURE_KEY[route.name])} />;
  }
}

function Shell() {
  return (
    <AlarmCountProvider>
      <ShellInner />
    </AlarmCountProvider>
  );
}

function ShellInner() {
  const { t } = useI18n();
  const { count: alarmCount } = useAlarmCount();
  const { tenant, data, dataState, reload } = useSession();
  const route = useRoute();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const railRef = useRef<HTMLElement>(null);
  const behindRef = useRef<HTMLDivElement>(null);
  const activeHref = activeHrefFor(route);
  const crumbs = useMemo(() => crumbsFor(route, data, tenant?.name ?? "", t), [route, data, tenant, t]);

  // Phones and tablets: the rail is an off-canvas drawer with a focus trap (ADR 0030).
  const drawer = useDrawer({ open: drawerOpen, onClose: () => setDrawerOpen(false), panel: railRef, initial: closeRef, behind: behindRef, main: mainRef });

  useEffect(() => {
    // A route change closes the drawer as a navigation: focus goes to the new page, not back to the menu button.
    drawer.navigated();
    setDrawerOpen(false);
    document.documentElement.scrollTop = 0;
  }, [route.path, route.query.toString()]);

  // Ctrl/⌘ + K: quick switcher over screens, sites, vehicles and devices already loaded (ADR 0029).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isQuickSearchShortcut(e)) return;
      // Never stack the switcher on another modal (a form in progress, a confirmation, a one-time secret).
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      setQuickOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const last = crumbs[crumbs.length - 1]?.label;
    document.title = last ? `${last} · Forklift Takip Sistemi` : "Forklift Takip Sistemi";
  }, [crumbs]);

  let content;
  if (!tenant) content = <EmptyState icon="building" title={t("tenant.none")} />;
  else if (dataState === "error")
    content = (
      <EmptyState
        icon="alert"
        title={t("common.loadError")}
        action={
          <button type="button" className="button button-secondary" onClick={reload}>
            {t("common.retry")}
          </button>
        }
      />
    );
  else if (!data || dataState === "loading")
    content = (
      <Loading shape="cards" />
    );
  else content = <Screen route={route} data={data} />;

  return (
    <div className={`app${drawerOpen ? " drawer-open" : ""}`}>
      <button type="button" className="skip-link" onClick={() => mainRef.current?.focus()}>
        {t("app.skipToContent")}
      </button>
      <aside
        id="rail"
        ref={railRef}
        className="rail"
        aria-label={t("nav.ariaLabel")}
        {...(drawerOpen ? { role: "dialog", "aria-modal": true } : {})}
      >
        <button ref={closeRef} type="button" className="icon-button rail-close" onClick={() => setDrawerOpen(false)} title={t("nav.closeMenu")}>
          <Icon name="close" size={20} label={t("nav.closeMenu")} />
        </button>
        <Rail activeHref={activeHref} alarmCount={alarmCount ?? 0} onNavigate={(h) => {
          if (window.location.hash !== h) window.location.hash = h.slice(1);
          drawer.navigated();
          setDrawerOpen(false);
        }} />
      </aside>
      <div className="scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      <div className="content" ref={behindRef}>
        <TopBar crumbs={crumbs} route={route} alarmCount={alarmCount ?? 0} onOpenMenu={() => setDrawerOpen(true)} menuOpen={drawerOpen} onOpenQuick={() => setQuickOpen(true)} />
        <main id="main" ref={mainRef} tabIndex={-1} className="main">
          <div className="main-inner">{content}</div>
        </main>
        <BottomBar activeHref={activeHref} alarmCount={alarmCount ?? 0} onOpenMenu={() => setDrawerOpen(true)} menuOpen={drawerOpen} />
      </div>
      {quickOpen && data && (
        <QuickSearch
          route={route}
          onClose={() => setQuickOpen(false)}
          onNavigate={(h) => {
            if (window.location.hash !== h) window.location.hash = h.slice(1);
          }}
        />
      )}
    </div>
  );
}

function Gate() {
  const { status } = useSession();
  const { t } = useI18n();
  const route = useRoute();
  // The invitation page works signed in and signed out, outside the console shell.
  if (route.name === "invite") return <InvitePage token={route.params.token ?? ""} />;
  // The QR checklist form works without a console session (the token is the credential; ADR 0016).
  if (route.name === "checklistForm") return <ChecklistFormPage token={route.params.token ?? ""} />;
  if (status === "loading")
    return (
      <div className="boot" role="status">
        <span className="spinner" aria-hidden="true" />
        <span className="sr-only">{t("common.loading")}</span>
      </div>
    );
  if (status === "signedOut") return <Login />;
  return <Shell />;
}

export function App({ provider, initialLang }: { provider?: DataProvider; initialLang?: Lang }) {
  const p = useMemo(() => provider ?? createProvider(), [provider]);
  return (
    <I18nProvider initialLang={initialLang}>
      <ToastProvider>
        <SessionProvider provider={p}>
          <Gate />
          <PwaStatus />
        </SessionProvider>
      </ToastProvider>
    </I18nProvider>
  );
}
